import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError, api } from "@/lib/api";
import { startAuthentication } from "@simplewebauthn/browser";
import { useBranding } from "@/lib/branding";
import { Eye, EyeOff, ShieldCheck, Lock, Fingerprint, MapPin, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

function RequestAccessModal({ open, onOpenChange }) {
  const [mode, setMode] = useState("request");
  const [form, setForm] = useState({ name: "", email: "", class_year: "", message: "" });
  const [claimEmail, setClaimEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@")) { toast.error("Please enter your name and a valid email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/access-requests", form);
      toast.success(data.message || "Request sent to the alumni office.");
      setForm({ name: "", email: "", class_year: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err) || "Could not send your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  const submitClaim = async (e) => {
    e.preventDefault();
    if (!claimEmail.includes("@")) { toast.error("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/claim-request", { email: claimEmail });
      if (data.ok === false) {
        toast.error(data.message || "We couldn't process that request.");
      } else if (data.email_paused) {
        toast.warning(data.message || "We found your record, but email is currently paused.", { duration: 8000 });
        setClaimEmail("");
        onOpenChange(false);
      } else {
        toast.success(data.message || "If a record exists, we've emailed you a claim link.");
        setClaimEmail("");
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(apiError(err) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="request-access-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{mode === "request" ? "Request access" : "Claim my record"}</DialogTitle>
          <DialogDescription>
            {mode === "request"
              ? "New to the registry? Send your details and the alumni office will set up your account."
              : "Already have a record on file? Enter your email and we'll send you a link to claim your profile."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted mb-1" data-testid="access-mode-toggle">
          {[["request", "Request access"], ["claim", "Claim my record"]].map(([k, label]) => (
            <button key={k} type="button" data-testid={`access-mode-${k}`} onClick={() => setMode(k)}
              className={`py-1.5 rounded-md text-sm font-medium transition-colors ${mode === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>

        {mode === "request" ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="ra-name">Full name</Label>
              <Input id="ra-name" data-testid="ra-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ra-email">Email</Label>
              <Input id="ra-email" data-testid="ra-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ra-year">Class year <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="ra-year" data-testid="ra-year" value={form.class_year} onChange={set("class_year")} placeholder="1990" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ra-message">Anything we should know? <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea id="ra-message" data-testid="ra-message" value={form.message} onChange={set("message")} rows={3} placeholder="Maiden name, degree, or how we can verify you…" className="mt-1.5" />
            </div>
            <DialogFooter>
              <button type="submit" data-testid="ra-submit" disabled={busy} className="w-full py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                {busy ? "Sending…" : "Send request"}
              </button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={submitClaim} className="space-y-4">
            <div>
              <Label htmlFor="claim-email">Email on file</Label>
              <Input id="claim-email" data-testid="claim-email" type="email" value={claimEmail} onChange={(e) => setClaimEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" />
              <p className="mt-1.5 text-xs text-muted-foreground">Use the email your alumni office has for you. We'll email a secure link to set up your login.</p>
            </div>
            <DialogFooter>
              <button type="submit" data-testid="claim-submit" disabled={busy} className="w-full py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                {busy ? "Sending…" : "Email me my claim link"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const UD_SEAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png";

export default function Login() {
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [ip, setIp] = useState("");
  const [requestOpen, setRequestOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [videoVer, setVideoVer] = useState(0);
  const [bioAvailable, setBioAvailable] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const videoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/promo-video${videoVer ? `?v=${videoVer}` : ""}`;

  useEffect(() => {
    const saved = localStorage.getItem("ud_remember_email");
    if (saved) { setEmail(saved); setRemember(true); }
    api.get("/whoami").then((r) => setIp(r.data.ip)).catch(() => {});
    api.get("/promo-video/available").then((r) => { setHasVideo(!!r.data.available); setVideoVer(r.data.version || 0); }).catch(() => {});
    if (window.PublicKeyCredential && PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().then((ok) => setBioAvailable(!!ok)).catch(() => {});
    }
    if (new URLSearchParams(window.location.search).get("timeout")) {
      toast.info("You were signed out after 30 minutes of inactivity.");
    }
  }, []);

  // Ask the server whether the typed email actually has a passkey registered here.
  // The button only turns green ("Ready") when a passkey exists for that account.
  useEffect(() => {
    const e = (email || "").trim().toLowerCase();
    if (!e.includes("@")) { setHasPasskey(false); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.post("/auth/passkeys/check", { email: e, origin: window.location.origin })
        .then((r) => { if (!cancelled) setHasPasskey(!!r.data.exists); })
        .catch(() => { if (!cancelled) setHasPasskey(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [email]);

  const passkeyLogin = async () => {
    if (!email) { setErr("Enter your email first, then use your passkey."); return; }
    setErr(""); setBusy(true);
    try {
      const opts = (await api.post("/auth/passkeys/login/options", { email, origin: window.location.origin })).data;
      const cred = await startAuthentication({ optionsJSON: opts });
      const { data } = await api.post("/auth/passkeys/login/verify", { origin: window.location.origin, credential: cred });
      const u = await loginWithToken(data.token);
      toast.success(`Welcome back, ${u.name}`);
      navigate(["admin", "readonly", "superadmin", "alumni_manager"].includes(u.role) ? "/" : (localStorage.getItem("ud_alumni_last_tab") || "/experience"));
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || (e2.name === "NotAllowedError" ? "Passkey sign-in was cancelled." : e2.message));
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      if (remember) localStorage.setItem("ud_remember_email", email);
      else localStorage.removeItem("ud_remember_email");
      toast.success(`Welcome back, ${u.name}`);
      navigate(["admin", "readonly", "superadmin", "alumni_manager"].includes(u.role) ? "/" : (localStorage.getItem("ud_alumni_last_tab") || "/experience"));
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const b = useBranding();

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-secondary">
      {b._resolved && b.logo_secondary ? <img src={b.logo_secondary} alt="" aria-hidden="true" className="hidden lg:block pointer-events-none select-none fixed left-1/2 bottom-0 -translate-x-1/2 h-[92vh] w-auto opacity-90 drop-shadow-2xl z-10" /> : null}
      <div className="hidden lg:block relative">
        {b.login_bg ? <img src={b.login_bg} alt="Campus" className="absolute inset-0 h-full w-full object-cover" /> : null}
        <div className="absolute inset-0 bg-gradient-to-tr from-secondary/95 via-secondary/75 to-secondary/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-transparent to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-full bg-white flex items-center justify-center p-1 shadow-md">
              {b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : null}
            </div>
            <div className="leading-tight">
              <div className="font-serif text-xl drop-shadow">{b.university_name}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent drop-shadow">{b.tagline}</div>
            </div>
          </div>
          <div>
            <div className="h-1 w-16 rounded-full bg-accent mb-5" />
            <h1 className="font-serif text-4xl xl:text-5xl leading-tight tracking-tight drop-shadow-lg [text-shadow:0_2px_18px_rgba(0,0,0,0.45)]">{b.login_headline}</h1>
            <p className="mt-4 text-white/85 max-w-md drop-shadow [text-shadow:0_1px_10px_rgba(0,0,0,0.4)]">{b.login_subtext}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a href="/learn-more" data-testid="login-learn-more" className="inline-flex items-center gap-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 px-4 py-2 rounded-md backdrop-blur transition-colors">
                Learn more about the platform <span aria-hidden="true">→</span>
              </a>
              {hasVideo && (
                <button type="button" data-testid="login-video-highlights" onClick={() => setVideoOpen(true)} className="inline-flex items-center gap-2 text-sm font-medium text-white bg-accent/90 hover:bg-accent px-4 py-2 rounded-md backdrop-blur transition-colors">
                  <PlayCircle className="h-4 w-4" /> Quick Video Highlights
                </button>
              )}
            </div>
          </div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/55 drop-shadow">{b.university_name} · Office of Alumni Relations</div>
        </div>
      </div>

      <div className="flex flex-col bg-background relative z-30 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-accent/[0.055]" />
          {b.logo_primary ? <img src={b.logo_primary} alt="" className="absolute -bottom-40 -right-40 w-[620px] max-w-none opacity-[0.05] grayscale select-none" /> : null}
          {b.logo_primary ? <img src={b.logo_primary} alt="" className="absolute -top-28 -left-28 w-[360px] max-w-none opacity-[0.04] grayscale select-none" /> : null}
        </div>
        <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <form onSubmit={submit} className="w-full max-w-sm fade-up bg-card rounded-2xl border border-border/60 ring-1 ring-black/5 shadow-[0_24px_60px_-18px_rgba(0,26,75,0.35)] overflow-hidden p-7 sm:p-8 relative before:absolute before:inset-x-0 before:top-0 before:h-1.5 before:bg-gradient-to-r before:from-primary before:via-accent before:to-primary" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            {b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-12 w-12 object-contain" /> : null}
            <div className="leading-tight">
              <div className="font-serif text-xl">{b.university_name}</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Alumni Registry</div>
            </div>
          </div>
          <h2 className="font-serif text-3xl tracking-tight">Sign in</h2>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Access the alumni registry.</p>

          {err && (
            <div data-testid="login-error" className="mb-4 text-sm bg-destructive/10 text-destructive rounded-md px-3 py-2">{err}</div>
          )}

          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full mb-4 px-3 py-2.5 rounded-md border border-input bg-card focus:ring-2 focus:ring-accent focus:outline-none"
            placeholder="you@school.edu"
          />
          <label className="block text-sm font-medium mb-1">Password</label>
          <div className="relative mb-2">
            <input
              data-testid="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2.5 pr-10 rounded-md border border-input bg-card focus:ring-2 focus:ring-accent focus:outline-none"
              placeholder="••••••••"
            />
            <button
              type="button"
              data-testid="toggle-password"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="mb-4 text-right">
            <button type="button" data-testid="forgot-password-link" onClick={() => navigate("/forgot-password")} className="text-sm text-primary hover:underline">Forgot password?</button>
          </div>
          <label className="flex items-center gap-2 mb-6 text-sm text-muted-foreground select-none cursor-pointer">
            <input
              type="checkbox"
              data-testid="remember-email"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 accent-[#001A4B]"
            />
            Remember my email
          </label>
          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full py-2.5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <button
            data-testid="passkey-login"
            type="button"
            onClick={passkeyLogin}
            disabled={busy}
            className={`w-full mt-3 py-2.5 rounded-md font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60 border ${hasPasskey ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98] shadow-sm" : "border-primary text-primary hover:bg-primary/5"}`}
          >
            <Fingerprint className="h-4 w-4" /> Sign in with Face ID / fingerprint
            {hasPasskey && <span className="ml-1.5 inline-flex items-center rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide">Ready</span>}
          </button>
          {hasPasskey ? (
            <p data-testid="passkey-hint" className="mt-1.5 text-center text-xs text-emerald-700">Passkey found for this account — tap to sign in</p>
          ) : bioAvailable ? (
            <p data-testid="passkey-hint" className="mt-1.5 text-center text-xs text-muted-foreground">Enter your email to use a saved passkey</p>
          ) : null}

          <p className="mt-4 text-center text-sm text-muted-foreground">
            No account yet?{" "}
            <button type="button" data-testid="request-access-link" onClick={() => setRequestOpen(true)} className="text-primary font-medium hover:underline">
              Request access
            </button>
          </p>

          <div className="mt-6 pt-4 border-t border-border" data-testid="security-panel">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-medium text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Secure connection</span>
              <span className="flex items-center gap-1.5"><Lock className="h-4 w-4 text-emerald-600" /> Encrypted (TLS)</span>
              <span className="flex items-center gap-1.5"><Fingerprint className="h-4 w-4 text-emerald-600" /> Brute-force protected</span>
            </div>
            <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" /> Your IP address:
              <span data-testid="client-ip" className="font-mono text-foreground">{ip || "detecting…"}</span>
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground/70">This device's IP is recorded for security. Unauthorized access is prohibited.</p>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {b.privacy_url ? (
                <a data-testid="privacy-notice-link" href={b.privacy_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Notice</a>
              ) : (
                <a data-testid="privacy-notice-link" href="/privacy" className="text-primary hover:underline">Privacy Notice</a>
              )}
              <span className="mx-1.5">·</span>
              We never sell or share your information.
            </p>
          </div>
        </form>
        </div>
        <Footer />
      </div>
      <RequestAccessModal open={requestOpen} onOpenChange={setRequestOpen} />
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent data-testid="video-highlights-modal" className="max-w-4xl p-0 overflow-hidden bg-black border-0">
          <DialogHeader className="sr-only"><DialogTitle>Quick Video Highlights</DialogTitle><DialogDescription>A short tour of the platform</DialogDescription></DialogHeader>
          {videoOpen && (
            <video data-testid="highlights-player" src={videoUrl} poster={b.login_bg || undefined} controls autoPlay playsInline className="w-full h-auto max-h-[80vh] bg-black">
              Your browser does not support the video tag.
            </video>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
